import {
  getAllCompositeSegments,
  getAllSegments,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { CompositionConfig } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import {
  ClipDeletes,
  ClipEdits
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { updateClipData } from './clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from './clipUpdateDataUtils/constants';
import { filterAndAdjustElementTracksForSubClip, getCompositeSegment } from './playerConfigUtil';
import { getPlayerTimeFromSegmentTime } from './timeUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

type ClipMetadata = Clip['asset_metadata'];

export interface TrimmedClipForCreate {
  /** Player config trimmed to [startTime, endTime] with overlays re-based to the sub-clip window. */
  playerConfig: CompositionConfig | null;
  edits: ClipEdits;
  deletes: ClipDeletes;
  duration: ClipMetadata['duration'];
  captions: ClipMetadata['captions'];
  start: number;
  end: number;
  /** Earliest composite-segment start (falls back to trimmed clip start for a single composite). */
  minStartTime: number;
  /** Latest composite-segment end (falls back to trimmed clip end for a single composite). */
  maxEndTime: number;
}

/**
 * Builds the trimmed `player_config` (and the derived metadata) for a clip that is being created
 * from a `[startTime, endTime]` selection, so the new sub-clip renders identically to its source.
 *
 * This centralises the trim logic that was previously duplicated (and divergent) between
 * `useCreateClip`'s splits branch and `CreateClipSelected`. It:
 *   1. Adds composite-segment deletes for everything after and before the selection (the non-splits
 *      handler is range-based and ignores the segment ids; the splits handler requires them).
 *   2. Optionally re-applies the layout (some callers do this, some don't — hence `layout` is optional).
 *   3. ALWAYS re-bases overlay (ELEMENTS) tracks via `filterAndAdjustElementTracksForSubClip`: the
 *      splits regeneration corrupts them through double-processing, while the non-splits regeneration
 *      leaves them at their original absolute frames — both are fixed by rebuilding from the original.
 *
 * Returns `null` when a trimmed config cannot be built safely (splits clip without segment ids, or a
 * clip with no segments); callers then create the clip without injecting a `player_config`.
 */
export function buildTrimmedClipForCreate({
  clip,
  startTime,
  endTime,
  startSegmentId,
  endSegmentId,
  fps
}: {
  clip: Clip;
  startTime: number;
  endTime: number;
  startSegmentId?: string;
  endSegmentId?: string;
  fps: number;
}): TrimmedClipForCreate | null {
  const splitsEnabled = isSplitsInTimelineEnabledPure({
    clip,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  // The splits composite-delete handler is segment-based and throws on empty ids. Without valid ids we
  // cannot safely trim, so bail out and let the caller create the clip without a trimmed player_config.
  if (splitsEnabled && (!startSegmentId || !endSegmentId)) {
    return null;
  }

  const allSegments = getAllSegments(clip);
  if (allSegments.length === 0) {
    return null;
  }

  const lastSegment = allSegments[allSegments.length - 1];
  const compositeMaxVideoDuration = clip?.asset_metadata?.maxVideoDuration;
  const lastSegmentEndTime =
    lastSegment.type === SegmentType.COMPOSITE || lastSegment.type === SegmentType.VIDEO
      ? lastSegment.segment_metadata?.maxVideoDuration || compositeMaxVideoDuration
      : lastSegment.toFrame / fps;

  // Trim everything after the selection, then (nested, on that result) everything before it. Both runs
  // use `skipStoreAndBackendUpdate` so nothing touches the store/backend — safe in read-only/embed UIs.
  const trimmedClip = updateClipData(
    {
      clipId: clip.id,
      actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
      startTime: endTime,
      startSegmentId: endSegmentId || '',
      endSegmentId: lastSegment.id || '',
      endTime: lastSegmentEndTime || 0,
      deleteType: 'MANUAL'
    },
    {
      currentClipData: updateClipData(
        {
          clipId: clip.id,
          actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
          startTime: 0,
          startSegmentId: allSegments[0].id,
          endSegmentId: startSegmentId || '',
          endTime: startTime,
          deleteType: 'MANUAL'
        },
        { skipStoreAndBackendUpdate: true }
      ),
      skipStoreAndBackendUpdate: true
    }
  ) as Clip;

  // Overlay frames are measured against the ORIGINAL clip. For splits, use the selection's segment ids;
  // for non-splits, resolve the single composite id (passing '' would return 0 and drop every overlay).
  const frameSegmentId = splitsEnabled ? undefined : getCompositeSegment(clip)?.id;

  // If the composite id can't be resolved in the non-splits path, we'd fall back to '' below and reframe
  // every overlay to [0, 0] (effectively dropping them). Bail out so the caller skips the player_config
  // rather than silently producing a corrupt one.
  if (!splitsEnabled && !frameSegmentId) {
    return null;
  }

  const playerStartFrame =
    getPlayerTimeFromSegmentTime(startTime, clip, (splitsEnabled ? startSegmentId : frameSegmentId) || '') * fps;
  const playerEndFrame =
    getPlayerTimeFromSegmentTime(endTime, clip, (splitsEnabled ? endSegmentId : frameSegmentId) || '') * fps;

  const playerConfig = filterAndAdjustElementTracksForSubClip(
    trimmedClip.asset_metadata.player_config,
    clip.asset_metadata.player_config,
    playerStartFrame,
    playerEndFrame
  );

  const compositeSegments = getAllCompositeSegments(trimmedClip);
  const minStartTime =
    compositeSegments.length > 1
      ? Math.min(...compositeSegments.map(seg => seg.segment_metadata?.start || 0))
      : trimmedClip.asset_metadata.start;
  const maxEndTime =
    compositeSegments.length > 1
      ? Math.max(...compositeSegments.map(seg => seg.segment_metadata?.end || 0))
      : trimmedClip.asset_metadata.end;

  return {
    playerConfig,
    edits: trimmedClip.asset_metadata.edits,
    deletes: trimmedClip.asset_metadata.deletes || {},
    duration: trimmedClip.asset_metadata.duration,
    captions: trimmedClip.asset_metadata.captions,
    start: trimmedClip.asset_metadata.start,
    end: trimmedClip.asset_metadata.end,
    minStartTime,
    maxEndTime
  };
}
